import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Switch, FormControlLabel, Box, Stack
} from '@mui/material';
import { Shift, CreateShiftRequest } from '../../interfaces/shift/Shift';
import { ShiftService } from '../../services/shift.service';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';

interface Props {
    open: boolean;
    onClose: () => void;
    shift?: Shift | null;
    institutionId: string;
    onSaved: () => void;
}

interface FormState {
    name: string;
    code: string;
    startTime: string;
    endTime: string;
    graceMinutesIn: string;
    graceMinutesOut: string;
    otThresholdMinutes: string;
    otMaxMinutes: string;
    isActive: boolean;
}

interface FormErrors {
    name?: string;
    code?: string;
    startTime?: string;
    endTime?: string;
    graceMinutesIn?: string;
    graceMinutesOut?: string;
    otThresholdMinutes?: string;
    otMaxMinutes?: string;
}

const CODE_REGEX = /^[A-Z0-9_]+$/;

// Convert "HH:mm:ss" → "HH:mm" for MUI time input
function toTimeInput(t: string): string {
    return t ? t.substring(0, 5) : '';
}

// Convert "HH:mm" → "HH:mm:ss" for backend
function toBackendTime(t: string): string {
    return t ? `${t}:00` : '';
}

const emptyForm = (): FormState => ({
    name: '',
    code: '',
    startTime: '',
    endTime: '',
    graceMinutesIn: '0',
    graceMinutesOut: '0',
    otThresholdMinutes: '0',
    otMaxMinutes: '',
    isActive: true,
});

const shiftToForm = (s: Shift): FormState => ({
    name: s.name,
    code: s.code,
    startTime: toTimeInput(s.startTime),
    endTime: toTimeInput(s.endTime),
    graceMinutesIn: String(s.graceMinutesIn),
    graceMinutesOut: String(s.graceMinutesOut),
    otThresholdMinutes: String(s.otThresholdMinutes),
    otMaxMinutes: s.otMaxMinutes != null ? String(s.otMaxMinutes) : '',
    isActive: s.isActive,
});

const ShiftDetailDialog: React.FC<Props> = ({ open, onClose, shift, institutionId, onSaved }) => {
    const toast = useToast();
    const { t } = useTranslation(['shifts', 'common']);
    const [form, setForm] = useState<FormState>(emptyForm());
    const [errors, setErrors] = useState<FormErrors>({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setForm(shift ? shiftToForm(shift) : emptyForm());
            setErrors({});
        }
    }, [open, shift]);

    const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
        setForm(prev => ({ ...prev, [key]: value }));
        setErrors(prev => ({ ...prev, [key]: undefined }));
    };

    const validate = (): boolean => {
        const errs: FormErrors = {};

        if (!form.name.trim()) errs.name = t('shiftDetail.errors.nameRequired');
        if (!form.code.trim()) {
            errs.code = t('shiftDetail.errors.codeRequired');
        } else if (!CODE_REGEX.test(form.code)) {
            errs.code = t('shiftDetail.errors.codeFormat');
        }
        if (!form.startTime) errs.startTime = t('shiftDetail.errors.startRequired');
        if (!form.endTime) errs.endTime = t('shiftDetail.errors.endRequired');
        if (form.startTime && form.endTime && form.startTime === form.endTime) {
            errs.endTime = t('shiftDetail.errors.timesMustDiffer');
        }
        const numericFields: (keyof Pick<FormState, 'graceMinutesIn' | 'graceMinutesOut' | 'otThresholdMinutes'>)[] = [
            'graceMinutesIn', 'graceMinutesOut', 'otThresholdMinutes',
        ];
        for (const f of numericFields) {
            const val = Number(form[f]);
            if (isNaN(val) || val < 0) {
                errs[f] = t('shiftDetail.errors.nonNegative');
            }
        }
        if (form.otMaxMinutes !== '' && (isNaN(Number(form.otMaxMinutes)) || Number(form.otMaxMinutes) < 0)) {
            errs.otMaxMinutes = t('shiftDetail.errors.otMaxNonNegative');
        }

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        const body: CreateShiftRequest = {
            name: form.name.trim(),
            code: form.code.trim(),
            startTime: toBackendTime(form.startTime),
            endTime: toBackendTime(form.endTime),
            graceMinutesIn: Number(form.graceMinutesIn),
            graceMinutesOut: Number(form.graceMinutesOut),
            otThresholdMinutes: Number(form.otThresholdMinutes),
            otMaxMinutes: form.otMaxMinutes !== '' ? Number(form.otMaxMinutes) : null,
            isActive: form.isActive,
        };

        setSaving(true);
        try {
            if (shift) {
                await ShiftService.update(institutionId, shift.id, body);
                toast.showSuccess(t('shiftDetail.updated'));
            } else {
                await ShiftService.create(institutionId, body);
                toast.showSuccess(t('shiftDetail.created'));
            }
            onSaved();
            onClose();
        } catch (err) {
            toast.showApiError(err, t('shiftDetail.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{shift ? t('shiftDetail.editTitle') : t('shiftDetail.newTitle')}</DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 1 }}>
                    <Stack spacing={2}>
                        <TextField
                            label={t('common:fields.name')}
                            fullWidth
                            value={form.name}
                            onChange={e => setField('name', e.target.value)}
                            error={!!errors.name}
                            helperText={errors.name}
                            required
                            inputProps={{ 'data-testid': 'shift-name' }}
                        />
                        <TextField
                            label={t('shiftDetail.code')}
                            fullWidth
                            value={form.code}
                            onChange={e => setField('code', e.target.value.toUpperCase())}
                            error={!!errors.code}
                            helperText={errors.code || t('shiftDetail.codeHelp')}
                            required
                            inputProps={{ 'data-testid': 'shift-code' }}
                        />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label={t('shiftDetail.startTime')}
                                type="time"
                                fullWidth
                                value={form.startTime}
                                onChange={e => setField('startTime', e.target.value)}
                                error={!!errors.startTime}
                                helperText={errors.startTime}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ 'data-testid': 'shift-start-time' }}
                            />
                            <TextField
                                label={t('shiftDetail.endTime')}
                                type="time"
                                fullWidth
                                value={form.endTime}
                                onChange={e => setField('endTime', e.target.value)}
                                error={!!errors.endTime}
                                helperText={errors.endTime}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ 'data-testid': 'shift-end-time' }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label={t('shiftDetail.graceIn')}
                                type="number"
                                fullWidth
                                value={form.graceMinutesIn}
                                onChange={e => setField('graceMinutesIn', e.target.value)}
                                error={!!errors.graceMinutesIn}
                                helperText={errors.graceMinutesIn}
                                inputProps={{ min: 0, 'data-testid': 'shift-grace-in' }}
                            />
                            <TextField
                                label={t('shiftDetail.graceOut')}
                                type="number"
                                fullWidth
                                value={form.graceMinutesOut}
                                onChange={e => setField('graceMinutesOut', e.target.value)}
                                error={!!errors.graceMinutesOut}
                                helperText={errors.graceMinutesOut}
                                inputProps={{ min: 0, 'data-testid': 'shift-grace-out' }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label={t('shiftDetail.otThreshold')}
                                type="number"
                                fullWidth
                                value={form.otThresholdMinutes}
                                onChange={e => setField('otThresholdMinutes', e.target.value)}
                                error={!!errors.otThresholdMinutes}
                                helperText={errors.otThresholdMinutes}
                                inputProps={{ min: 0, 'data-testid': 'shift-ot-threshold' }}
                            />
                            <TextField
                                label={t('shiftDetail.otMax')}
                                type="number"
                                fullWidth
                                value={form.otMaxMinutes}
                                onChange={e => setField('otMaxMinutes', e.target.value)}
                                error={!!errors.otMaxMinutes}
                                helperText={errors.otMaxMinutes}
                                inputProps={{ min: 0, 'data-testid': 'shift-ot-max' }}
                            />
                        </Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.isActive}
                                    onChange={e => setField('isActive', e.target.checked)}
                                    data-testid="shift-active"
                                />
                            }
                            label={t('common:status.active')}
                        />
                    </Stack>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>{t('common:actions.cancel')}</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={saving}>
                    {saving ? t('saving') : t('common:actions.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ShiftDetailDialog;
